// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web) — éléments communs : garde de route, erreurs du serveur, états, navigation
// d'une organisation, pagination, fenêtres de confirmation et de motif, dépôt et liste de pièces.
// Composants définis hors de tout parent (react-component-rules, règle 1).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Download, Paperclip, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { estExterne } from '../../utils/pageAccueil';
import { Badge, Bouton, BandeauInfo, EtatVide, Pagination, Squelette, formaterDate } from '../../design/composants';
import { Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import { useDs, erreurDe, toArray, libelle, TYPES_PIECE, taillePiece, dateHeure } from './api';

export { Fenetre, Champ, PiedFenetre };

/** Garde des écrans : compte connecté et interne. Les droits fins sont vérifiés par le serveur. */
export function Protege({ children }) {
  const { employe, loading } = useAuth();
  if (loading) return <div className="rh-page" style={{ padding: 24 }}><Squelette hauteur={20} largeur="40%" /></div>;
  if (!employe) return <Navigate to="/login" replace />;
  if (estExterne(employe)) return <Navigate to="/ipm" replace />;
  return children;
}

const TITRES_ERREUR = {
  DOUBLE_REGARD: 'Double regard obligatoire',
  ACTE_SUR_SOI_INTERDIT: 'Acte sur son propre dossier interdit',
  MANDAT_REQUIS: 'Mandat requis',
  PERMISSION_REQUISE: 'Habilitation requise',
  COMPTE_TECHNIQUE_EXCLU: 'Compte non nominatif',
  HORS_PERIMETRE: 'Hors de votre organisation',
  FONCTION_OCCUPEE: 'Fonction déjà occupée',
  CUMUL_INTERDIT: 'Cumul de fonctions interdit',
  PIECE_ALTEREE: 'Pièce altérée',
};
const COMPLEMENTS = {
  DOUBLE_REGARD: 'Règle du double regard : une même personne ne peut pas accomplir deux étapes incompatibles d\'un même dossier (préparer puis valider, enregistrer puis confirmer).',
  ACTE_SUR_SOI_INTERDIT: 'Personne n\'agit sur son propre dossier, quels que soient sa fonction, un intérim ou une délégation.',
};
const titreErreur = (e) => TITRES_ERREUR[e.code]
  || (e.statut === 403 ? 'Accès refusé' : e.statut === 404 ? 'Introuvable' : e.statut === 409 ? 'Opération impossible dans l\'état actuel'
    : e.statut === 400 ? 'Informations à corriger' : e.statut === 429 ? 'Trop de demandes' : e.statut === 0 ? 'Serveur injoignable' : 'Erreur');

/** Erreur du serveur affichée telle quelle (titre selon le code, message et détails du serveur). */
export function ErreurServeur({ erreur, action }) {
  if (!erreur) return null;
  const e = erreur.statut !== undefined ? erreur : erreurDe(erreur);
  return (
    <BandeauInfo ton={e.statut === 409 || e.statut === 400 ? 'alerte' : 'danger'} titre={titreErreur(e)} action={action}>
      <span title={e.code || undefined}>{e.message}</span>
      {COMPLEMENTS[e.code] && <span style={{ display: 'block', marginTop: 4, color: 'var(--sg-texte-2)' }}>{COMPLEMENTS[e.code]}</span>}
      {e.erreurs.length > 0 && <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{e.erreurs.map((x) => <li key={String(x)}>{String(x)}</li>)}</ul>}
    </BandeauInfo>
  );
}

/** Lecture refusée (403) : écran propre, sans données. */
export function AccesRefuseDs({ erreur }) {
  return <EtatVide Icone={ShieldAlert} titre={titreErreur(erreur)} texte={erreur.message} />;
}

/** Chargement d'une lecture : { donnees, erreur (normalisée), recharger }. */
export function useLecture(charger, deps) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const recharger = useCallback(async () => {
    try { setErreur(null); setDonnees(await charger()); } catch (e) { setErreur(erreurDe(e, 'Chargement impossible.')); setDonnees(null); }
  }, deps);
  useEffect(() => { recharger(); }, [recharger]);
  return { donnees, erreur, recharger };
}

/** État coloré (point + texte) à partir d'une table de libellés { CODE: [ton, libellé] }. */
export function Etat({ table, code }) {
  const [ton, lib] = table[code] || ['neutre', code || ''];
  return <Badge ton={ton} point>{lib}</Badge>;
}

const MODULES_ORGANISATION = [['prets', 'Prêts'], ['aides', 'Aides'], ['campagnes', 'Campagnes'], ['tresorerie', 'Trésorerie'], ['comptabilite', 'Comptabilité'], ['retenues', 'Retenues'], ['rapports', 'Rapports']];

/** Onglets d'une organisation (liens) ; `modules` : onglets métier visibles (prêts, aides…) selon les pouvoirs. */
export function NavOrganisation({ orgId, actif, modules = [] }) {
  const liens = [
    ['instances', 'Instances et mandats'], ['dispositifs', 'Dispositifs'],
    ...MODULES_ORGANISATION.filter(([cle]) => modules.includes(cle) || actif === cle),
    ['a-traiter', 'À traiter'], ['historique', 'Historique'], ['pieces', 'Pièces'],
  ];
  const refActif = useRef(null);
  // Onglet courant visible sur petit écran (la barre d'onglets défile horizontalement).
  useEffect(() => { if (refActif.current && refActif.current.scrollIntoView) refActif.current.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }, [actif]);
  return (
    <nav className="rh-onglets" aria-label="Sections de l'organisation">
      {liens.map(([cle, lib]) => (
        <Link key={cle} ref={actif === cle ? refActif : undefined} to={`/dispositifs/${orgId}/${cle}`} className="rh-onglet" aria-current={actif === cle ? 'page' : undefined}
          style={{ textDecoration: 'none', ...(actif === cle ? { borderBottomColor: 'var(--sg-primaire)', color: 'var(--sg-primaire)', fontWeight: 500 } : {}) }}>
          {lib}
        </Link>
      ))}
    </nav>
  );
}

/** Pagination d'une page serveur { page, taille, total }. */
export function PaginationDs({ donnees, onPage }) {
  if (!donnees || !donnees.total) return null;
  const pages = Math.max(1, Math.ceil(donnees.total / donnees.taille));
  return <Pagination page={donnees.page} pages={pages} total={donnees.total} taille={donnees.taille} onPage={(p) => onPage(Math.min(Math.max(1, p), pages))} />;
}

/** Confirmation simple d'un acte (sans saisie). */
export function FenetreConfirmer({ titre, Icone, texte, libelleBouton, onFermer, onConfirmer }) {
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const valider = async () => {
    setEnCours(true); setErreur(null);
    try { await onConfirmer(); } catch (e) { setErreur(erreurDe(e)); setEnCours(false); }
  };
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      {texte && <p style={{ margin: '0 0 12px', fontSize: 14 }}>{texte}</p>}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Icone} disabled={enCours} onClick={valider}>{libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Acte motivé (motif obligatoire, longueur minimale imposée par le serveur). */
export function FenetreMotif({ titre, Icone, aide, libelleBouton, minimum = 3, danger = false, onFermer, onConfirmer }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const valider = async () => {
    setEnCours(true); setErreur(null);
    try { await onConfirmer(motif.trim()); } catch (e) { setErreur(erreurDe(e)); setEnCours(false); }
  };
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      {aide && <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>{aide}</p>}
      <Champ libelle="Motif" requis aide={`${minimum} caractères au moins. Le motif est conservé dans l'historique.`}>
        <textarea className="rh-champ-saisie" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante={danger ? 'danger' : 'primaire'} Icone={Icone} disabled={enCours || motif.trim().length < minimum} onClick={valider}>{libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

const FORMATS_ACCEPTES = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const TAILLE_MAX = 10 * 1024 * 1024;

/**
 * Choix d'une pièce justificative à déposer : fichier (PDF, JPEG, PNG, 10 Mo), type, libellé.
 * `valeur` = { fichier, typePiece, libelle } ; une fois déposée, la pièce (id) est affichée et n'est plus redéposée.
 */
export function ChoixPiece({ valeur, onChange, types, deposee, requis = true, libelleChamp = 'Pièce justificative' }) {
  const f = valeur.fichier;
  const tropGros = f && f.size > TAILLE_MAX;
  if (deposee) {
    return (
      <Champ libelle={libelleChamp}>
        <div style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
          <Paperclip size={14} aria-hidden="true" />{deposee.libelle} — déposée, empreinte {String(deposee.empreinte_sha256 || '').slice(0, 12)}…
        </div>
      </Champ>
    );
  }
  return (
    <fieldset style={{ border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '10px 12px 0', margin: '0 0 12px' }}>
      <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', padding: '0 4px' }}>
        {libelleChamp}{requis && <span className="rh-form-requis" aria-hidden="true">*</span>}
      </legend>
      <Champ libelle="Fichier" requis={requis} aide="PDF, JPEG ou PNG, 10 Mo au plus. Une pièce déposée ne se supprime jamais.">
        <input type="file" accept={FORMATS_ACCEPTES} className="rh-champ-saisie" onChange={(e) => onChange({ ...valeur, fichier: e.target.files?.[0] || null })} />
      </Champ>
      {tropGros && <p style={{ margin: '-6px 0 10px', fontSize: 13, color: 'var(--sg-danger)' }}>Ce fichier dépasse 10 Mo.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 12 }}>
        <Champ libelle="Type" requis={requis}>
          <select className="rh-champ-saisie" value={valeur.typePiece} onChange={(e) => onChange({ ...valeur, typePiece: e.target.value })}>
            {types.map((t) => <option key={t} value={t}>{libelle(TYPES_PIECE, t)}</option>)}
          </select>
        </Champ>
        <Champ libelle="Libellé" requis={requis}>
          <input className="rh-champ-saisie" value={valeur.libelle} maxLength={200} placeholder="Ex. : PV de l'assemblée générale du 12 mars"
            onChange={(e) => onChange({ ...valeur, libelle: e.target.value })} />
        </Champ>
      </div>
    </fieldset>
  );
}
export const pieceComplete = (v) => !!(v.fichier && v.fichier.size <= TAILLE_MAX && v.typePiece && v.libelle.trim().length >= 2);

/** Ouvre une pièce téléchargée (nouvel onglet) ; l'URL temporaire est libérée ensuite. */
export function ouvrirBlob({ blob }, nom) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.download = nom;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
}

/** Une ligne de pièce : métadonnées lues au serveur (refus affiché tel quel), téléchargement si autorisé. */
function LignePiece({ id, contexte }) {
  const ds = useDs();
  const [p, setP] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [erreurFichier, setErreurFichier] = useState(null);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    let vivant = true;
    ds.piece(id).then((x) => { if (vivant) setP(x); }).catch((e) => { if (vivant) setErreur(erreurDe(e)); });
    return () => { vivant = false; };
  }, [ds, id]);
  const telecharger = async () => {
    setEnCours(true); setErreurFichier(null);
    try {
      const f = await ds.fichierPiece(id);
      const ext = p.type_mime === 'application/pdf' ? '.pdf' : p.type_mime === 'image/png' ? '.png' : '.jpg';
      ouvrirBlob(f, `${(p.libelle || 'piece').replace(/[^\w\-À-ÿ ]+/g, '').trim().slice(0, 60) || 'piece'}${ext}`);
    } catch (e) { setErreurFichier(e.normalisee || erreurDe(e)); }
    setEnCours(false);
  };
  if (erreur) {
    return (
      <tr>
        <td colSpan={5}><span className="rh-cellule-secondaire">{contexte ? `${contexte} — ` : ''}pièce non consultable : {erreur.message}</span></td>
      </tr>
    );
  }
  if (!p) return <tr><td colSpan={5}><Squelette largeur="50%" /></td></tr>;
  return (
    <tr>
      <td>
        <span className="rh-cellule-principale">{p.libelle}</span>
        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{libelle(TYPES_PIECE, p.type_piece)}{contexte ? ` · ${contexte}` : ''}{p.donnees_demo ? ' · [DÉMO]' : ''}</span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(p.deposee_le)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{taillePiece(p.taille_octets)}</span></td>
      <td>
        <code title={`Empreinte SHA-256 complète : ${p.empreinte_sha256}`} style={{ fontSize: 12, color: 'var(--sg-texte-2)', wordBreak: 'break-all' }}>
          {String(p.empreinte_sha256).slice(0, 16)}…
        </code>
      </td>
      <td>{p.statut === 'ACTIVE' ? <Badge ton="succes" point>Active</Badge> : <Badge ton="neutre" point>Remplacée</Badge>}
        {p.conserver_jusqu_au && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Conservée jusqu'au {formaterDate(p.conserver_jusqu_au)}</span>}
      </td>
      <td style={{ textAlign: 'right' }}>
        <Bouton petit variante="discret" Icone={Download} disabled={enCours} onClick={telecharger}>Télécharger</Bouton>
        {erreurFichier && <div style={{ fontSize: 12, color: 'var(--sg-danger)', marginTop: 4, maxWidth: 260, marginLeft: 'auto' }}>{erreurFichier.message}</div>}
      </td>
    </tr>
  );
}

/**
 * Pièces d'un dossier : `pieces` = [{ id, contexte }] (sans doublon). Chaque pièce est relue au serveur, qui
 * vérifie l'accès ; l'empreinte SHA-256 est vérifiée par le serveur à chaque téléchargement.
 */
export function ListePieces({ pieces, vide = 'Aucune pièce rattachée à ce dossier.' }) {
  const uniques = toArray(pieces).filter((x, i, arr) => x && x.id && arr.findIndex((y) => y.id === x.id) === i);
  if (!uniques.length) return <EtatVide Icone={Paperclip} titre="Aucune pièce" texte={vide} />;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Pièce</th><th>Déposée le</th><th>Empreinte</th><th>État</th><th aria-label="Actions" /></tr></thead>
        <tbody>{uniques.map((x) => <LignePiece key={x.id} id={x.id} contexte={x.contexte} />)}</tbody>
      </table>
    </div>
  );
}
